import type { AccessDirectory } from "@shared/access/directory";
import {
  grantProblems,
  type PersonalLevelSetting,
} from "@shared/access/grants";
import { levelCap, type HomeKind } from "@shared/access/homeKind";
import { reaches } from "@shared/access/levels";
import type { Place } from "@shared/access/places";
import type { RoleName } from "@shared/access/roles";
import type { GrantProblem } from "@shared/access/grants";
import { ownerHomeProblem, type HomeChoice } from "@shared/access/userAccess";
import type { Translate } from "../../i18n/translate";
import {
  grantsFromForm,
  levelLabel,
  resourceLabel,
  roleLabel,
  type GrantForm,
} from "./grantForm";

export function nendaPlaceLabel(t: Translate): string {
  return t("users:formChecks.nendaPlace");
}

export function nendaPlaceProblem(t: Translate): string {
  return t("users:formChecks.nendaPlaceProblem");
}

/** The sentence for a refused grant, from the problem's code and values. */
export function grantProblemText(t: Translate, problem: GrantProblem): string {
  switch (problem.code) {
    case "role_above_limit":
      return t("users:formChecks.problems.role_above_limit", {
        role: roleLabel(t, problem.role),
        limit: levelLabel(t, problem.limit),
      });
    case "level_above_limit":
      return t("users:formChecks.problems.level_above_limit", {
        level: levelLabel(t, problem.level),
        resource: resourceLabel(t, problem.resource),
        limit: levelLabel(t, problem.limit),
      });
    case "unknown_role":
      return t("users:formChecks.problems.unknown_role", {
        role: problem.role,
      });
    case "unknown_resource":
      return t("users:formChecks.problems.unknown_resource", {
        resource: problem.resource,
      });
    case "unknown_level":
      return t("users:formChecks.problems.unknown_level", {
        level: problem.level,
      });
    case "repeated_resource":
      return t("users:formChecks.problems.repeated_resource", {
        resource: problem.resource,
      });
  }
}

export function nendaPlace(directory: AccessDirectory): Place {
  return { kind: "company", id: directory.nendaCompanyId };
}

function isNendaPlace(place: Place, directory: AccessDirectory): boolean {
  return place.kind === "company" && place.id === directory.nendaCompanyId;
}

export function roleTooHigh(role: RoleName | null, kind: HomeKind): boolean {
  return (
    role !== null &&
    grantProblems({ role, personalLevels: [] }, kind).some(
      (p) => p.code === "role_above_limit"
    )
  );
}

export interface FittedForm {
  readonly form: GrantForm;
  readonly removed: readonly string[];
}

export function fitFormToHome(
  t: Translate,
  form: GrantForm,
  kind: HomeKind,
  directory: AccessDirectory
): FittedForm {
  const removed = new Set<string>();
  const cap = levelCap(kind);
  const fitRole = (role: RoleName | null) => {
    if (!roleTooHigh(role, kind)) return role;
    removed.add(
      t("users:formChecks.removedRole", { role: roleLabel(t, role) })
    );
    return null;
  };
  const fitLevels = (levels: readonly PersonalLevelSetting[]) =>
    levels.filter((p) => {
      if (p.level === "none" || reaches(cap, p.level)) return true;
      removed.add(
        t("users:formChecks.removedLevel", {
          level: levelLabel(t, p.level),
          resource: resourceLabel(t, p.resource),
        })
      );
      return false;
    });
  const keepPlace = (place: Place) => {
    if (kind === "nenda" || !isNendaPlace(place, directory)) return true;
    removed.add(
      t("users:formChecks.removedPlace", { place: nendaPlaceLabel(t) })
    );
    return false;
  };

  let places = form.main.places.filter(keepPlace);
  if (kind === "nenda" && places.length === 0) places = [nendaPlace(directory)];
  const fitted: GrantForm = {
    main: {
      role: fitRole(form.main.role),
      personalLevels: fitLevels(form.main.personalLevels),
      places,
    },
    exceptions: form.exceptions
      .filter((e) => keepPlace(e.place))
      .map((e) => ({
        ...e,
        role: fitRole(e.role),
        personalLevels: fitLevels(e.personalLevels),
      })),
  };
  return { form: fitted, removed: [...removed] };
}

export function homeChangeNote(
  t: Translate,
  removed: readonly string[]
): string | null {
  if (removed.length === 0) return null;
  return t("users:formChecks.homeChangeNote", { removed });
}

export function formProblems(
  t: Translate,
  form: GrantForm,
  kind: HomeKind,
  directory: AccessDirectory,
  home: HomeChoice | null = null
): string[] {
  const problems = new Set<string>();
  const { main } = form;
  if (main.places.length === 0 && main.role !== null) {
    problems.add(
      t("users:formChecks.choosePlaceForRole", {
        role: roleLabel(t, main.role),
      })
    );
  } else if (main.places.length === 0 && main.personalLevels.length > 0) {
    problems.add(t("users:formChecks.choosePlaceForLevels"));
  }
  const grants = grantsFromForm(form);
  if (
    kind !== "nenda" &&
    [...main.places, ...form.exceptions.map((e) => e.place)].some((p) =>
      isNendaPlace(p, directory)
    )
  ) {
    problems.add(nendaPlaceProblem(t));
  }
  for (const g of grants) {
    for (const p of grantProblems(g, kind))
      problems.add(grantProblemText(t, p));
  }
  const ownerProblem = ownerHomeProblem(home, kind, grants);
  if (ownerProblem) problems.add(t(`users:formChecks.${ownerProblem}`));
  return [...problems];
}

export function inviteProblems(
  t: Translate,
  role: RoleName | null,
  kind: HomeKind
): string[] {
  if (role === null) return [];
  const problems = grantProblems({ role, personalLevels: [] }, kind).map((p) =>
    grantProblemText(t, p)
  );
  if (kind === "content-owner" && role !== "content-owner") {
    problems.push(t("users:formChecks.role_not_for_content_owner"));
  }
  return problems;
}
